import { Popover } from "antd";
import {
  UserGroupCUSystem,
  UserGroupCUSystemCamera,
  UserGroupSystemCamera,
} from "api/interfaces/userInterface.interface";
import { useEffect, useMemo, useState } from "react";
import { IoMdImage } from "react-icons/io";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { MdBrokenImage } from "react-icons/md";
import { COLORS } from "styles/colors";
import { checkCovaPermissionFirmwareVersion } from "utils/RecorderUtil";

export type SearchRecorderProps = {
  mergedSystemId: string;
  firmwareVersion: string;
  displayType: string;
  isDisabled: boolean;
  cameraList: UserGroupSystemCamera[];
  selectedCameraList?: UserGroupCUSystemCamera[];
  onChange: (system: UserGroupCUSystem) => void;
};

export default function CameraList(props: SearchRecorderProps): JSX.Element {
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const canSelectCamera =
    props.displayType !== "COVA" || checkCovaPermissionFirmwareVersion(props);

  const selectableCameras = useMemo(
    () => props.cameraList.filter((camera) => !camera.isDisabled),
    [props.cameraList]
  );

  useEffect(() => {
    setSelectedChannels(
      (props.selectedCameraList ?? [])
        .filter((camera) => camera.isAccess)
        .map((camera) => camera.channel)
    );
  }, [props.selectedCameraList]);

  const emitChange = (channels: number[]) => {
    const nextCameras = selectableCameras.map(
      (camera): UserGroupCUSystemCamera => ({
        channel: camera.channel,
        cameraId: camera.cameraId,
        isAccess: channels.includes(camera.channel),
        cameraUuid: camera.cameraUuid,
      })
    );

    props.onChange({
      mergedSystemId: props.mergedSystemId,
      displayType: props.displayType,
      allCameras:
        nextCameras.length > 0 &&
        nextCameras.every((camera) => camera.isAccess),
      cameras: nextCameras,
    });
  };

  const onToggleCamera = (channel: number, checked: boolean) => {
    const nextSelectedChannels = checked
      ? Array.from(new Set([...selectedChannels, channel]))
      : selectedChannels.filter(
          (selectedChannel) => selectedChannel !== channel
        );

    setSelectedChannels(nextSelectedChannels);
    emitChange(nextSelectedChannels);
  };

  return (
    <Wrapper>
      {props.cameraList.map((camera: UserGroupSystemCamera) => {
        const hasCameraId = !!camera.cameraId;
        const hasThumbnail = !!camera.thumbnailUrl;
        const isChecked = selectedChannels.includes(camera.channel);
        const cameraName = (
          <div className={`camName ${hasCameraId ? "clickable" : ""}`}>
            <span className="camName-text">CH {camera.channel + 1}</span>
            {hasCameraId &&
              (hasThumbnail ? (
                <span className="camName-thumb">
                  <IoMdImage size={12} />
                </span>
              ) : (
                <span className="camName-thumb">
                  <MdBrokenImage size={12} />
                </span>
              ))}
          </div>
        );

        return (
          <li key={`camera-channel-${camera.channel}`}>
            <label>
              {canSelectCamera && (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={camera.isDisabled || props.isDisabled}
                  onChange={(e) => {
                    onToggleCamera(camera.channel, e.target.checked);
                  }}
                  data-testid="element-search-user-group-recorder-input-toggle-camera"
                />
              )}

              {hasCameraId ? (
                <Popover
                  trigger="hover"
                  placement="right"
                  content={
                    hasThumbnail ? (
                      <ThumbnailWrapper>
                        <img src={camera.thumbnailUrl} alt="camera thumbnail" />
                      </ThumbnailWrapper>
                    ) : (
                      <ThumbnailWrapper className="thumbnail-fallback">
                        <MdBrokenImage size={28} />
                        <p className="warning-text">Thumbnail Not Available</p>
                      </ThumbnailWrapper>
                    )
                  }
                >
                  {cameraName}
                </Popover>
              ) : (
                cameraName
              )}
            </label>
          </li>
        );
      })}
    </Wrapper>
  );
}

const Wrapper = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 16px;

  padding: 14px 8px;
  margin-left: 30px;
  box-sizing: border-box;
  list-style: none;

  > li {
    label {
      gap: 8px;
      align-items: flex-start;

      input[type="checkbox"] {
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
        margin-top: 2px;
      }
      .camName {
        ${FLEX.FlexStartCenter}
        gap: 4px;
        cursor: default;
        &.clickable {
          cursor: pointer;
        }
        .camName-text {
          white-space: normal;
          word-break: break-word;
        }
        .camName-thumb {
          flex-shrink: 0;
          margin-top: 2px;
          color: ${({ theme }) => theme.COLORS.TEXT};
        }
      }
    }
  }
`;

const ThumbnailWrapper = styled.div`
  width: 360px;
  height: 200px;
  overflow: hidden;
  border-radius: 4px;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.thumbnail-fallback {
    ${FLEX.FlexCenterCenter}
    flex-direction: column;
    gap: 10px;
    background-color: ${COLORS.BLACK};
    color: ${COLORS.VALIDATERED};
    text-align: center;
  }

  .warning-text {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    letter-spacing: 0.02em;
  }
`;
